import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import client from '../api/client';
import BuyerLocationMap from './BuyerLocationMap';
import BuyerLocationDistance from './BuyerLocationDistance';

const TOP_N = 5;
const formatCount = (n) => new Intl.NumberFormat('en-US').format(n);

const BuyerLocationCard = ({ teamId, confirmId, buyerLocation }) => {
  const hasInjectedData = buyerLocation !== undefined;
  const shouldFetch = !hasInjectedData && Boolean(teamId) && Boolean(confirmId);

  const [fetchedData, setFetchedData] = useState(null);
  const [loading, setLoading] = useState(shouldFetch);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!shouldFetch) {
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    client
      .get(`/teams/${teamId}/confirms/${confirmId}/marketing_insights/buyer_location`, {
        signal: controller.signal,
      })
      .then((response) => setFetchedData(response.data.buyer_location))
      .catch((err) => {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        setError(err);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [shouldFetch, teamId, confirmId]);

  if (loading) {
    return (
      <section className="mi-audience-card mi-buyer-loc-card" aria-label="Buyer location">
        <p className="mi-audience-empty">Loading buyer location…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="mi-audience-card mi-buyer-loc-card" aria-label="Buyer location">
        <p className="mi-audience-empty">Couldn't load buyer location. Refresh to try again.</p>
      </section>
    );
  }

  const data = hasInjectedData ? buyerLocation : fetchedData;

  const cities = data?.cities || [];
  const venue = data?.venue || { name: null, latitude: null, longitude: null };
  const radiusMi = data?.radius_mi || 50;
  const breakdown = data?.distance_breakdown || [];
  const hasCities = cities.length > 0;
  const hasVenueGeo = venue.latitude != null && venue.longitude != null;
  const showMap = hasCities && hasVenueGeo;
  const showDistance = breakdown.length > 0;

  const visibleCities = expanded ? cities : cities.slice(0, TOP_N);
  const hiddenCount = cities.length - visibleCities.length;
  const venueSuffix = venue.name ? ` of ${venue.name}` : '';

  return (
    <section className="mi-audience-card mi-buyer-loc-card" aria-label="Buyer location">
      <div className="mi-buyer-loc-head">
        <h3 className="mi-audience-card-title">Buyer location</h3>
        {hasCities && (
          <span className="mi-audience-card-subtitle">
            Where your <strong>{formatCount(data.buyers)}</strong> buyer
            {data.buyers === 1 ? '' : 's'} are coming from — <strong>{data.pct_within_radius}% within {radiusMi} mi</strong>
            {venueSuffix}
          </span>
        )}
      </div>

      {!hasCities ? (
        <p className="mi-audience-empty">No buyer location data yet</p>
      ) : (
        <div className="mi-buyer-loc-body">
          {showMap && (
            <BuyerLocationMap
              venue={venue}
              cities={cities}
              radiusMi={radiusMi}
              pctWithinRadius={data.pct_within_radius}
            />
          )}

          <div className="mi-buyer-loc-list">
            <div className="mi-buyer-loc-colheads">
              <span className="mi-buyer-loc-rank" />
              <span className="mi-buyer-loc-colhead">City</span>
              <span className="mi-buyer-loc-colhead">Share of tickets</span>
              <span className="mi-buyer-loc-colhead mi-buyer-loc-colhead--right">Tix</span>
              <span className="mi-buyer-loc-colhead" />
            </div>

            {visibleCities.map((c, i) => (
              <div key={`${c.city}-${c.state}`} className="mi-buyer-loc-row">
                <span className="mi-buyer-loc-rank">{i + 1}</span>
                <span className="mi-buyer-loc-name">
                  {c.city} <span className="mi-buyer-loc-state">{c.state}</span>
                </span>
                <span className="mi-buyer-loc-bar-track">
                  <span className="mi-buyer-loc-bar" style={{ width: `${c.share_pct}%` }} />
                </span>
                <span className="mi-buyer-loc-tickets">{formatCount(c.tickets)}</span>
                <span className="mi-buyer-loc-share">{c.share_pct}%</span>
              </div>
            ))}

            {hiddenCount > 0 && (
              <button type="button" className="mi-buyer-loc-more" onClick={() => setExpanded(true)}>
                + {hiddenCount} more {hiddenCount === 1 ? 'city' : 'cities'}
              </button>
            )}

            <div className="mi-buyer-loc-footer">
              {formatCount(data.total_tickets)} tickets
              {data.unlocated_tickets > 0 && (
                <span className="mi-buyer-loc-unlocated"> · {formatCount(data.unlocated_tickets)} without location</span>
              )}
            </div>
          </div>

          {showDistance && (
            <BuyerLocationDistance breakdown={breakdown} insight={data.distance_insight} />
          )}
        </div>
      )}
    </section>
  );
};

BuyerLocationCard.propTypes = {
  teamId: PropTypes.number,
  confirmId: PropTypes.number,
  buyerLocation: PropTypes.shape({
    radius_mi: PropTypes.number,
    buyers: PropTypes.number,
    total_tickets: PropTypes.number,
    unlocated_tickets: PropTypes.number,
    pct_within_radius: PropTypes.number,
    venue: PropTypes.shape({
      name: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    cities: PropTypes.arrayOf(PropTypes.object),
    distance_breakdown: PropTypes.arrayOf(PropTypes.object),
    distance_insight: PropTypes.string,
  }),
};

BuyerLocationCard.defaultProps = {
  teamId: null,
  confirmId: null,
  buyerLocation: undefined,
};

export default BuyerLocationCard;
